import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { adWriteApi } from '../../../../api/adWriteApi';

import { useAdDetailsContext } from '../../../../contexts/AdDetailsContext';
import {
  useUserContext,
  useAuthOptions,
} from '../../../../contexts/UserContext';
import { useVerificationContext } from '../../../../contexts/VerificationContext';
import { useOnUpdateOnly } from '../../../../hooks/UseOnUpdateOnly';
import { useQueryParams } from '../../../../hooks/UseQueryParams';
import { useBrowserStorage } from '../../../../hooks/UseBrowserStorage';

import { fireToast } from '../../../../helpers/Toasts';
import { formatAuthorizationHeader } from '../../../../helpers/auth';
import { pushToDataLayer } from '../../../../services/gtmService';

import { type UseRevealDealerEnquiryModalParams } from '../../../../components/PageWrappers/SharedComponents/RevealDealerEnquiryModal/RevealDealerEnquiryModal.hook.typed';
import {
  MODAL_QUERY_KEY,
  MODAL_QUERY_VALUE,
} from './RevealDealerEnquiryModal.constants';
import { AUTHENTICATION_STATUSES } from '../../AdDetails/types';

const useRevealDealerEnquiryModal = (
  params: UseRevealDealerEnquiryModalParams,
) => {
  const { adId, accessToken } = params;

  const {
    setIsDealerEnquiryModalOpen,
    setEnquiry,
    enableLoggedOutDealerEnquiry,
  } = useAdDetailsContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();
  const { isUserVerified } = useUserContext();
  const { status, handleLogin } = useAuthOptions();

  const token = formatAuthorizationHeader(accessToken);

  const { query } = useRouter();
  const { addQueries, checkIfQueryIsPresent } = useQueryParams();
  const {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  } = useBrowserStorage();

  const MODAL_QUERY = { [MODAL_QUERY_KEY]: MODAL_QUERY_VALUE };

  const isModalQueryPresent = checkIfQueryIsPresent(
    MODAL_QUERY_KEY,
    MODAL_QUERY_VALUE,
  );

  const SESSION_STORAGE_SIGNIN_ITEM_NAME = 'signin';

  const addModalQuery = () => addQueries(MODAL_QUERY);

  const initialiseDealerEnquiryModal = async () => {
    if (
      enableLoggedOutDealerEnquiry &&
      status === AUTHENTICATION_STATUSES.UNAUTHENTICATED
    ) {
      setIsDealerEnquiryModalOpen(true);
      return;
    }
    try {
      const { data: initialValues } =
        await adWriteApi.getDealerEnquiryInitialValues(adId, token);
      if (initialValues) {
        // TODO: Add reducer and handle loading state here
        setEnquiry(initialValues);
        setIsDealerEnquiryModalOpen(true);
      }
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  const directUserToSigninPage = () => {
    setSessionStorageItem(SESSION_STORAGE_SIGNIN_ITEM_NAME, MODAL_QUERY_VALUE);
    handleLogin();
  };

  /* The user may navigate directly to the URL with the query parameter
   * that opens the URL which is why we should conduct the same checks we
   * would on click */
  const onOpen = async () => {
    if (
      enableLoggedOutDealerEnquiry &&
      status === AUTHENTICATION_STATUSES.UNAUTHENTICATED
    ) {
      setIsDealerEnquiryModalOpen(true);
      return;
    }
    // TODO: add loading state
    verifyUser(initialiseDealerEnquiryModal);
  };

  const handleReveal = (clickSource?: string) => {
    pushToDataLayer({ event: 'message_intent', source: clickSource ?? '' });
    if (
      enableLoggedOutDealerEnquiry &&
      status === AUTHENTICATION_STATUSES.UNAUTHENTICATED
    ) {
      addModalQuery();
      return;
    }
    verifyUser(addModalQuery);
  };

  const verifyUser = (callback: () => void | Promise<void>) => {
    if (status === AUTHENTICATION_STATUSES.AUTHENTICATED) {
      if (isUserVerified || enableLoggedOutDealerEnquiry) {
        callback();
      } else {
        fireToast({
          type: 'INFO',
          text: 'You will need to verify your phone or email before sending an enquiry',
        });
      }
    } else if (status === AUTHENTICATION_STATUSES.UNAUTHENTICATED) {
      directUserToSigninPage();
    }
  };

  useOnUpdateOnly(() => {
    if (triggerAfterVerify && isUserVerified) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
  }, [triggerAfterVerify]);

  /*
   * We update the URL to trigger the modal. Mobile users in particular interpret full-screen modals
   * as new pages and so are inclined to try to navigate back to close the modal. This ensures that
   * they remain on the same page that they opened the modal.
   *
   * It also allows us to direct the user to an ad details page with the modal already opem.
   *
   * However, if an unauthenticated user is logged out when they try to open the modal,
   * they are directed to the sign-in page. Once they are redirected to the page
   * on which they had opened the modal, we check to see if there is an item matching
   * the modal they had tried to open. If so, and they meet the conditions for opening the
   * modal, a modal query is added to the URL which is used to trigger the modal.
   * As a result, navigating back dismisses the modal.
   */
  useEffect(() => {
    const isReturningUser =
      getSessionStorageItem(SESSION_STORAGE_SIGNIN_ITEM_NAME) ===
        MODAL_QUERY_VALUE && status !== AUTHENTICATION_STATUSES.LOADING;

    if (isModalQueryPresent) {
      onOpen();
    } else if (isReturningUser) {
      /**
       * As well as making sure the session status is finished loading before performing an action,
       * we avoid adding a query altogether if the user is authenticated and verified and handle that scenario right away,
       * instead of waiting until the query is added.
       *
       * This check is also performed if the user decides to add the query manually,
       * so that only verified authenticated users will be able to open the modal.
       */
      if (status === AUTHENTICATION_STATUSES.AUTHENTICATED) {
        if (isUserVerified) {
          addModalQuery();
        } else {
          fireToast({
            type: 'INFO',
            text: 'You will need to verify your phone or email before sending an enquiry',
          });
        }
      }
      removeSessionStorageItem(SESSION_STORAGE_SIGNIN_ITEM_NAME);
    } else {
      setIsDealerEnquiryModalOpen(false);
    }
    // TODO Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, isUserVerified]);

  return {
    handleReveal,
  };
};

export { useRevealDealerEnquiryModal };
